import { Favorite, FavoriteBorder } from "@mui/icons-material";
import { Grid, IconButton, useTheme } from "@mui/material";
import React, { useEffect } from "react";

export const FavoriteToggle = ({
    favoriteFn,
    unfavoriteFn,
    isFavorited,
    id,
    variant
}: {
    favoriteFn: () => void;
    unfavoriteFn: () => void;
    isFavorited: boolean;
    id?: string;
    variant?: "default" | "thin" | "noBorder";
}) => {
    const theme = useTheme();
    const [favorited, setFavorited] = React.useState(isFavorited);
    const debounceTimeout = React.useRef<any>(null);

    // Update local state if isFavorited prop changes
    useEffect(() => {
        setFavorited(isFavorited);
    }, [isFavorited]);

    // Debounce effect: call favoriteFn/unfavoriteFn after delay
    useEffect(() => {
        if (favorited === isFavorited) return; // Only act if changed
        if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
        debounceTimeout.current = setTimeout(() => {
            if (favorited) {
                favoriteFn && favoriteFn();
            } else {
                unfavoriteFn && unfavoriteFn();
            }
        }, 500); // 500ms debounce
        return () => {
            if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
        };
    }, [favorited, favoriteFn, isFavorited, unfavoriteFn]);

    const defaultSx = {
        color: theme.palette.black.main,
        border: "1px solid",
        borderColor: theme.palette.gray.light,
        borderRadius: 1,
        backgroundColor: theme.palette.white.main,
    };

    const noBorderSx = {
        color: theme.palette.black.main,
        p: 0,
    };

    const thinSx = {
        color: theme.palette.black.main,
        height: "30px",
        border: "1px solid",
        borderColor: theme.palette.gray[200],
        borderRadius: 1,
        backgroundColor: theme.palette.white.main,
    }

    return (
        <Grid>
            <IconButton
                id={id}
                sx={variant === "thin" ? thinSx : variant === "noBorder" ? noBorderSx : defaultSx}
                onClick={() => {
                    setFavorited((prev) => !prev);
                }}
                size="small"
            >
                {favorited ? (
                    <Favorite color="primary" />
                ) : (
                    <FavoriteBorder color="disabled" />
                )}
            </IconButton>
        </Grid>
    );
};
